import type { SessionSummaryApi } from "@/Modules/Models/Api/ClinicalHistory";
import type { Year } from "@/Modules/Models/Domain/ClinicalHistory";
import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";

import { getSessions } from "@/Services/TherapyApi/ClinicalHistory";

import SessionTransformer, {
  type SessionTransformerType,
} from "@/Modules/Transformers/ClinicalHistory/Session";

import useClinicalHistoryStore from "@/Modules/ClinicalHistory/Store";
import useAppStore from "@/Modules/App/Store";
import useCustomerStore from "@/Modules/Customers/Store";
import useUserStore from "@/Modules/User/Store";

import { capitalizeFirstLetter } from "@/Utils/CapitalizeFirstLetter";
import { getTranslatedMonthName } from "@/Utils/Date";
import { danger } from "@/Utils/Debug";
import { isValidString } from "@/Utils/String";

async function getSessionsAH(customerId?: string) {
  const appStore = useAppStore();
  const clinicalHistoryStore = useClinicalHistoryStore();
  const customerStore = useCustomerStore();
  const userStore = useUserStore();
  const customerUuid = customerId ?? customerStore.getCurrentCustomer?.id;
  const sessionTransformer: SessionTransformerType = SessionTransformer;

  if (!isValidString(customerUuid)) return undefined;

  clinicalHistoryStore.isLoadingSessions = true;

  getSessions(customerUuid)
    .then((result) => {
      const summary: Year[] = [];

      result.body.years.forEach((yearArray: SessionSummaryApi) => {
        Object.entries(yearArray).forEach(([name, months]) => {
          const yearData: Year = { name, months: [] };
          const locale = userStore.user?.language;

          Object.entries(months).forEach(([monthNum, sessions]) => {
            const monthName = capitalizeFirstLetter(getTranslatedMonthName(parseInt(monthNum), locale));
            const monthData = {
              name: monthName,
              sessions: sessions.map(sessionTransformer.toDomain),
            };

            yearData.months.push(monthData);
          });

          summary.push(yearData);
        });
      });

      clinicalHistoryStore.sessionSummary = summary;
      clinicalHistoryStore.isTranscriptionEnabled = result.meta.is_enabled ?? false;
      clinicalHistoryStore.nextSessionAvailable = result.meta.next_session ?? null;
    })
    .catch((e) => {
      danger("Clinical history", e);
      appStore.showSnackbar({
        type: NotificationType.ERROR,
        message: "there_was_an_error_connecting_to_the_server_please_try_again_later",
      });
    })
    .finally(() => {
      clinicalHistoryStore.isLoadingSessions = false;
    });
}

export default getSessionsAH;
